import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, Subscriber } from 'rxjs';

import { TrialInsightsState } from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';

import { getScreenFailSummaryQuery, SortOrder } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { Utils } from '@shared/utils/utils';

import { TrialInsightsClinicalScreenFailChartService } from './screen-fail-chart.service';
import { TrialInsightsClinicalScreenFailTableService } from './screen-fail-table.service';
import {
  GenericTrialInsightsQuery,
  SortOrderDefault,
} from '../../classes/trial-insights-query.class';
import { TrialInsightsLegendDataOptions } from '../../models/trial-insights-legend.model';
import {
  TrialInsightsScreenFailKey,
  TrialInsightsTableOptions,
} from '../../models/trial-insights-table.model';

type Results = [State['data'], TrialInsightsScreenFailKey, SortOrder];
type RowData = TrialInsightsTableOptions['rowData']['data'];
type State = Partial<TrialInsightsState['screenFail']>;

const sortDefaults: SortOrderDefault[] = [
  {
    buttonKey: TrialInsightsScreenFailKey.NUMBER,
    defaultOrder: SortOrder.DESC,
  },
  {
    buttonKey: TrialInsightsScreenFailKey.PERCENT,
    defaultOrder: SortOrder.DESC,
  },
];

@Injectable()
export class TrialInsightsClinicalScreenFailQueryService extends GenericTrialInsightsQuery {
  chartService: TrialInsightsClinicalScreenFailChartService;
  tableService: TrialInsightsClinicalScreenFailTableService;
  trialInsightsQuery: TrialInsightsQuery;

  constructor() {
    const trialInsightsQuery = inject(TrialInsightsQuery);
    const tableService = inject(TrialInsightsClinicalScreenFailTableService);
    const chartService = inject(TrialInsightsClinicalScreenFailChartService);

    super({
      chartService,
      slice: 'screenFail',
      sortDefaults,
      tableService,
      trialInsightsQuery,
    });

    this.trialInsightsQuery = trialInsightsQuery;
    this.tableService = tableService;
    this.chartService = chartService;
  }

  processResponseData() {
    const processFn = (sourceValue: Results, subscriber: Subscriber<Results>) => {
      const [data, selectedKey, sortOrder] = sourceValue;

      if (!data || !data.length) {
        this.resetState();

        return;
      }

      const parsedData = this.parseStoreData(data);
      const screenFailData = this.parseStoreDetail(parsedData, selectedKey, sortOrder);

      if (!screenFailData) {
        return;
      }

      const screenFailCost = screenFailData.screen_fail_total_cost;

      this.totalAmount.next(Utils.currencyFormatter(screenFailCost));

      const screenFails = screenFailData.screen_fails;
      const screenFailRate = screenFailData.screen_fail_rate * 100;
      const dropoutRate = screenFailData.dropout_rate * 100;

      const legendData = [
        { title: 'Screen Fails', value: `${screenFails}` },
        { title: 'Screen Fail Rate', value: `${screenFailRate.toFixed(2)}%` },
        { displayDivider: false, title: 'Dropout Rate', value: `${dropoutRate.toFixed(2)}%` },
      ];

      const legend: TrialInsightsLegendDataOptions[] = legendData.map((legendOption) => {
        return {
          displayDivider: true,
          displayIcon: false,
          iconColor: '#094673',
          valueColor: '#094673',
          ...legendOption,
        };
      });

      const legendOptions = this.chartService.createLegend(legend);

      this.legendOptions.next(legendOptions);

      const screenFail =
        selectedKey === TrialInsightsScreenFailKey.NUMBER
          ? screenFailData.site_screen_fail_totals
          : screenFailData.site_screen_fail_rates;

      const rowData: RowData = screenFail.map((row) => {
        let screenFailAmount = 0;
        let screenFailFormatted = '';

        if ('screen_fail_rate' in row) {
          screenFailAmount = row.screen_fail_rate;
          screenFailFormatted = `${(screenFailAmount * 100).toFixed(2)}%`;
        } else if ('screen_fails' in row) {
          screenFailAmount = row.screen_fails;
          screenFailFormatted = `${screenFailAmount}`;
        }

        return {
          buttonKey: selectedKey,
          leftHeader: row.site_name || '',
          leftSubheader:
            row.primary_investigator?.given_name && row.primary_investigator?.family_name
              ? `${row.primary_investigator?.given_name} ${row.primary_investigator?.family_name}`
              : '',
          rightHeader: screenFailFormatted,
          rightSubheader: '',
        };
      });

      const tableOptions = this.tableService.createTable(selectedKey, rowData);

      this.tableOptions.next(tableOptions);

      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  processScreenFailSummary$(): Observable<Results> {
    return combineLatest([
      this.data as Observable<getScreenFailSummaryQuery[]>,
      this.selectedKey as Observable<TrialInsightsScreenFailKey>,
      this.sortOrder,
    ]).pipe(this.processResponseData());
  }
}
